import { all, call, fork, put, takeLatest } from 'redux-saga/effects';
import * as authActions from '../actions/Auth';
import * as authApi from '../api/Auth';
import { getCurrentUser } from '../actions/Profile';
import {
  showInfoNotification,
  showSuccessNotification,
  showErrorNotification,
} from '../actions/App';
import {
  ACCEPT_TERMS_AND_CONDITIONS,
  CONFIRM_USER_SIGNUP,
  DROP_SESSION,
  RESET_PASSWORD,
  SET_NEW_PASSWORD,
  SIGNIN_USER,
  SIGNOUT_USER,
  SUBMIT_TEMPORARY_PASSWORD,
  VERIFY_USER,
  VERIFY_USER_SIGNUP,
} from '../constants/ActionTypes';
import { AUTH_TOKEN_KEY, AUTH_ERROR_TYPES } from '../constants/Values';
import { WSUnsubscribe } from '../actions/WSNotifications';
import { addCookie, deleteCookie, getCookie } from '../util/cookies';
import { history } from '../store';

let preSavedResponse = null;

function* determineFurtherSignInInstructions(data = preSavedResponse) {
  if (Object.prototype.hasOwnProperty.call(data, 'termsAccepted') && !data.termsAccepted) {
    try {
      const termsResponse = yield call(authApi.getTermsAndConditions);
      yield put(
        authActions.showTermsAndConditions(
          data.accessToken,
          termsResponse.data.text,
          termsResponse.data.version,
        ),
      );
    } catch (error) {
      yield put(authActions.showAuthMessage(error.messages));
    }
  } else if (data.roles.includes('student')) {
    addCookie(AUTH_TOKEN_KEY, data.accessToken, true);
    yield put(authActions.signInSuccess(data.accessToken));
    yield put(getCurrentUser());
  } else {
    yield put(authActions.enterTemporaryPassword(data.accessToken));
  }
}

function* acceptTerms({ payload }) {
  try {
    yield call(authApi.acceptTermsAndConditions, payload.accessToken, payload.version);

    if (preSavedResponse) {
      preSavedResponse.termsAccepted = true;
    }

    yield call(determineFurtherSignInInstructions);
  } catch (error) {
    yield put(authActions.showAuthMessage(error.messages));
  }
}

function* confirmUserSignUp({ payload }) {
  const { identifier, password, code } = payload;
  try {
    const response = yield call(authApi.confirmSignUp, identifier, password, code);
    preSavedResponse = response.data;
    yield call(determineFurtherSignInInstructions, response.data);
  } catch (error) {
    if (error.errors) {
      yield put(
        authActions.showAuthValidationErrorMessage(
          AUTH_ERROR_TYPES.NEW_PASS,
          error.errors.find(err => err.fieldName === 'password').message,
        ),
      );
    } else {
      yield put(authActions.showAuthMessage(error.messages));
    }
  }
}

function* dropSession() {
  if (getCookie(AUTH_TOKEN_KEY)) {
    deleteCookie(AUTH_TOKEN_KEY);
    yield call(history.push, '/');
    yield put(showErrorNotification('errors.sessionExpired'));
  }
}

function* resetPassword({ payload }) {
  const { login } = payload;

  yield put(authActions.showAuthLoader());

  try {
    yield authApi.resetPassword(login);
    yield put(authActions.userResetPasswordStatus('pending'));
    yield put(showInfoNotification('appModule.resetPasswordCodeSent'));
  } catch (error) {
    if (error.status === 401) {
      yield put(
        authActions.showAuthValidationErrorMessage(
          AUTH_ERROR_TYPES.IDENTIFIER,
          'auth.errors.noIdentifier',
        ),
      );
    } else {
      yield put(showErrorNotification(error.messages));
    }
  }

  yield put(authActions.hideAuthLoader());
}

function* setNewPassword({ payload }) {
  const { login, code, password } = payload;

  yield put(authActions.showAuthLoader());

  try {
    yield authApi.setNewPassword(login, code, password);
    yield put(authActions.userResetPasswordStatus('success'));
    yield put(showSuccessNotification('appModule.passwordChanged'));
  } catch (error) {
    if (error.errors) {
      yield put(
        authActions.showAuthValidationErrorMessage(
          AUTH_ERROR_TYPES.NEW_PASS,
          error.errors.find(err => err.fieldName === 'password').message,
        ),
      );
    } else if (error.status === 401) {
      yield put(
        authActions.showAuthValidationErrorMessage(
          AUTH_ERROR_TYPES.VERIFICATION_CODE,
          'auth.errors.wrongVerificationCode',
        ),
      );
    } else {
      yield put(showErrorNotification(error.messages));
    }
  }

  yield put(authActions.hideAuthLoader());
}

function* signInUser({ payload }) {
  const { identifier, password } = payload;
  try {
    const response = yield call(authApi.signIn, identifier, password);
    preSavedResponse = response.data;
    yield call(determineFurtherSignInInstructions, response.data);
  } catch (error) {
    if (error.errors) {
      yield put(
        authActions.showAuthValidationErrorMessage(
          AUTH_ERROR_TYPES.IDENTIFIER_PASS,
          error.errors.find(err => err.fieldName === 'password').message,
        ),
      );
    } else {
      yield put(authActions.showAuthMessage(error.messages));
    }
  }
}

function* signOut() {
  try {
    yield call(authApi.signOutUser);
    yield put(WSUnsubscribe());
  } catch (error) {
    // ignore
  } finally {
    deleteCookie(AUTH_TOKEN_KEY);
    yield put(authActions.userSignOutSuccess());
    yield call(history.push, '/');
  }
}

function* submitTempPassword({ payload }) {
  const { accessToken, temporaryPassword } = payload;
  try {
    yield call(authApi.confirmSignIn, accessToken, temporaryPassword);
    addCookie(AUTH_TOKEN_KEY, accessToken, true);
    yield put(authActions.signInSuccess(accessToken));
    yield put(getCurrentUser());
  } catch (error) {
    if (error.status === 401) {
      yield put(
        authActions.showAuthValidationErrorMessage(
          AUTH_ERROR_TYPES.TEMPORARY_PASS,
          'auth.errors.wrongTemporaryPassword',
        ),
      );
    } else {
      yield put(authActions.showAuthMessage(error.messages));
    }
  }
}

function* verifyUser({ payload }) {
  try {
    yield call(authApi.verifyUserExistence, payload.identifier);
    try {
      yield call(authApi.verifyUserRegistration, payload.identifier);
      yield put(authActions.verifyUserSuccess(true));
    } catch (error) {
      yield call(authApi.signUp, payload.identifier);
      yield put(authActions.verifyUserSuccess(false));
    }
  } catch (error) {
    if (error.status === 404) {
      yield put(
        authActions.showAuthValidationErrorMessage(AUTH_ERROR_TYPES.IDENTIFIER, error.messages[0]),
      );
    } else {
      yield put(authActions.showAuthMessage(error.messages));
    }
  }
}

function* verifyUserSignUp({ payload }) {
  const { identifier, code } = payload;
  try {
    yield call(authApi.verifySignUp, identifier, code);
    yield put(authActions.verifyUserSignUpSuccess());
  } catch (error) {
    if (error.status === 400) {
      yield put(
        authActions.showAuthValidationErrorMessage(
          AUTH_ERROR_TYPES.VERIFICATION_CODE,
          'auth.errors.wrongVerificationCode',
        ),
      );
    } else {
      yield put(authActions.showAuthMessage(error.messages));
    }
  }
}

export function* acceptTermsSaga() {
  yield takeLatest(ACCEPT_TERMS_AND_CONDITIONS, acceptTerms);
}

export function* confirmUserSignUpSaga() {
  yield takeLatest(CONFIRM_USER_SIGNUP, confirmUserSignUp);
}

export function* dropSessionSaga() {
  yield takeLatest(DROP_SESSION, dropSession);
}

export function* resetPasswordSaga() {
  yield takeLatest(RESET_PASSWORD, resetPassword);
}

export function* setNewPasswordSaga() {
  yield takeLatest(SET_NEW_PASSWORD, setNewPassword);
}

export function* signInUserSaga() {
  yield takeLatest(SIGNIN_USER, signInUser);
}

export function* signOutSaga() {
  yield takeLatest(SIGNOUT_USER, signOut);
}

export function* submitTempPasswordSaga() {
  yield takeLatest(SUBMIT_TEMPORARY_PASSWORD, submitTempPassword);
}

export function* verifyUserSaga() {
  yield takeLatest(VERIFY_USER, verifyUser);
}

export function* verifyUserSignUpSaga() {
  yield takeLatest(VERIFY_USER_SIGNUP, verifyUserSignUp);
}

export default function* rootSaga() {
  yield all([
    fork(acceptTermsSaga),
    fork(confirmUserSignUpSaga),
    fork(dropSessionSaga),
    fork(resetPasswordSaga),
    fork(setNewPasswordSaga),
    fork(signInUserSaga),
    fork(signOutSaga),
    fork(submitTempPasswordSaga),
    fork(verifyUserSaga),
    fork(verifyUserSignUpSaga),
  ]);
}
